import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Loader2, UserPlus, MapPin, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only manual mechanic creation. Lightweight, mobile-first, uses the
 * existing /api/admin/mechanics/create endpoint (Firebase mirror handled
 * server-side).
 */
export default function AdminCreateMechanic() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    skills: "",
    lat: "",
    lng: "",
    approved: true,
    // Identity verification (admin-visible only)
    idNumber: "",
    driverLicenceNumber: "",
    tradeQualificationNumber: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS unavailable");
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setForm((f) => ({ ...f, lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }));
        setGeoLoading(false);
      },
      () => { setGeoLoading(false); toast.error("Couldn't get GPS"); },
      { timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const idTrim = (form.idNumber || "").replace(/\s+/g, "");
    const licTrim = (form.driverLicenceNumber || "").replace(/\s+/g, "").toUpperCase();
    if (!/^\d{13}$/.test(idTrim)) {
      setError("SA ID number must be exactly 13 digits.");
      return;
    }
    if (!/^[A-Z0-9]{5,15}$/.test(licTrim)) {
      setError("Driver's licence must be 5–15 letters/digits.");
      return;
    }
    const tradeTrim = (form.tradeQualificationNumber || "").trim();
    if (tradeTrim && !/^[A-Za-z0-9 _.\-/]{3,40}$/.test(tradeTrim)) {
      setError("Trade qualification must be 3–40 characters.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      password: form.password,
      skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      approved: !!form.approved,
      idNumber: idTrim,
      driverLicenceNumber: licTrim,
    };
    if (tradeTrim) payload.tradeQualificationNumber = tradeTrim;
    if (form.lat && form.lng) {
      payload.location = { lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/mechanics/create", payload);
      toast.success(`Mechanic created: ${data.user.name}`);
      navigate("/app/admin/users");
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <Link to="/app/admin/users" data-testid="admin-create-back-btn"><ArrowLeft /></Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          Admin · New Mechanic
        </span>
      </div>

      <div className="px-6 pt-2 pb-6">
        <div className="w-12 h-12 rounded-md bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center mb-3 text-[#3CFF7E]">
          <UserPlus size={20} />
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-tight">Add a Mechanic</h1>
        <p className="text-sm text-[#9CA3AF] mt-1">Create a mechanic account on behalf of a partner. They can log in with this email and PIN.</p>
      </div>

      <form onSubmit={submit} className="px-6 space-y-4">
        <Field label="Full name" testid="amec-name">
          <input required value={form.name} onChange={setField("name")} placeholder="Sipho Mokoena" className="afr-input" data-testid="amec-name-input" />
        </Field>
        <Field label="Phone (+27…)" testid="amec-phone">
          <input required type="tel" value={form.phone} onChange={setField("phone")} placeholder="+27 82 555 0100" className="afr-input" data-testid="amec-phone-input" />
        </Field>
        <Field label="Email" testid="amec-email">
          <input required type="email" autoComplete="off" value={form.email} onChange={setField("email")} placeholder="sipho@partner.co.za" className="afr-input" data-testid="amec-email-input" />
        </Field>
        <Field label="Skills / specialisation (comma-separated)" testid="amec-skills">
          <input value={form.skills} onChange={setField("skills")} placeholder="towing, battery, brakes" className="afr-input" data-testid="amec-skills-input" />
        </Field>

        {/* Identity verification — required for mechanics, admin-visible only */}
        <div className="pt-1" data-testid="amec-verification-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3CFF7E] font-display mb-1">
            Identity verification
          </div>
          <p className="text-[11px] text-[#9CA3AF] mb-2 leading-relaxed">
            Stored securely. Only admins can view these — never clients or other mechanics.
          </p>
          <Field label="SA ID number (13 digits)" testid="amec-id">
            <input
              required
              inputMode="numeric"
              maxLength={13}
              value={form.idNumber}
              onChange={setField("idNumber")}
              placeholder="9001015009087"
              className="afr-input tracking-wider"
              data-testid="amec-id-input"
            />
          </Field>
          <div className="mt-3">
            <Field label="Driver's licence (required)" testid="amec-licence">
              <input
                required
                maxLength={15}
                value={form.driverLicenceNumber}
                onChange={(e) => setForm({ ...form, driverLicenceNumber: e.target.value.toUpperCase() })}
                placeholder="Code 08 / 10 / 14 etc."
                className="afr-input tracking-wider"
                data-testid="amec-licence-input"
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Trade qualification (optional)" testid="amec-trade">
              <input
                maxLength={40}
                value={form.tradeQualificationNumber}
                onChange={setField("tradeQualificationNumber")}
                placeholder="e.g. RMI-2024-1234"
                className="afr-input"
                data-testid="amec-trade-input"
              />
            </Field>
          </div>
        </div>

        <Field label="Password / PIN" testid="amec-password">
          <div className="relative">
            <input
              required
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={form.password}
              onChange={setField("password")}
              placeholder="At least 4 characters"
              className="afr-input pr-10"
              data-testid="amec-password-input"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} aria-label="Toggle" className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-[#9CA3AF]">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>

        <Field label="Location (optional)" testid="amec-loc">
          <div className="flex items-center gap-2">
            <input value={form.lat} onChange={setField("lat")} placeholder="lat" inputMode="decimal" className="afr-input flex-1" data-testid="amec-lat-input" />
            <input value={form.lng} onChange={setField("lng")} placeholder="lng" inputMode="decimal" className="afr-input flex-1" data-testid="amec-lng-input" />
            <button
              type="button"
              onClick={useMyLocation}
              disabled={geoLoading}
              data-testid="amec-use-gps"
              className="h-12 px-3 border border-white/20 rounded-md flex items-center gap-1 text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50"
            >
              {geoLoading ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
              Use GPS
            </button>
          </div>
        </Field>

        <label className="flex items-center gap-3 border border-white/10 bg-[#111827] rounded-md p-3" data-testid="amec-approved-row">
          <input
            type="checkbox"
            checked={form.approved}
            onChange={(e) => setForm({ ...form, approved: e.target.checked })}
            data-testid="amec-approved-checkbox"
            className="w-5 h-5 accent-[#3CFF7E]"
          />
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-sm text-white">Approve immediately</div>
            <div className="text-xs text-[#9CA3AF]">
              {form.approved
                ? "Mechanic can go online + accept jobs right away."
                : "Mechanic will be created in pending state."}
            </div>
          </div>
        </label>

        {error && (
          <div className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3" data-testid="amec-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="amec-submit-btn"
          className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] disabled:opacity-60 shadow-[0_0_40px_rgba(60,255,126,0.35)] flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
          {loading ? "Creating…" : "Create Mechanic"}
        </button>
      </form>

      <style>{`
        .afr-input {
          height: 3rem;
          width: 100%;
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 0.375rem;
          padding: 0 1rem;
          color: #fff;
        }
        .afr-input::placeholder { color: #6B7280; }
        .afr-input:focus {
          outline: none;
          border-color: #3CFF7E;
          box-shadow: 0 0 0 2px rgba(60,255,126,0.2);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
